import { createInertiaApp } from '@inertiajs/react'
import { createRoot } from 'react-dom/client'
import axios from 'axios'

import { registerServiceWorker } from '../lib/registerServiceWorker'
import { I18nProvider, type Locale } from '../lib/i18n'
import { ToastProvider } from '../components/ui/Toast'

// Make the app installable (PWA) and enable the offline fallback page.
registerServiceWorker()

// Bridge Rails CSRF with Inertia/axios: the server sets an XSRF-TOKEN cookie
// (ApplicationController#set_csrf_cookie); axios reads it and sends it back under the
// header name Rails checks (X-CSRF-Token). See docs/decisions/0001.
axios.defaults.xsrfCookieName = 'XSRF-TOKEN'
axios.defaults.xsrfHeaderName = 'X-CSRF-Token'

void createInertiaApp({
  pages: "../pages",

  setup({ el, App, props }) {
    // The active locale rides along as an Inertia shared prop (ApplicationController). Pin it into
    // the provider once per full load — the switcher hard-reloads, so this re-runs on every change.
    const initialProps = (props as { initialPage: { props: { locale?: string } } }).initialPage.props
    const locale: Locale = initialProps.locale === 'zh' ? 'zh' : 'en'
    createRoot(el!).render(
      <I18nProvider locale={locale}>
        <ToastProvider>
          <App {...props} />
        </ToastProvider>
      </I18nProvider>,
    )
  },

  strictMode: true,

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" }
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
